import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Container,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Instagram } from "@mui/icons-material";
import { motion, useInView } from "framer-motion";
import logo from "../../assets/nav-lexmom.webp";
import { useNavigate } from "react-router-dom";
import XIcon from "@mui/icons-material/X";

const Footer = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const footerRef = useRef(null);
  const isInView = useInView(footerRef, { once: true, margin: "-100px" });
  const svgRef = useRef(null);

  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [hoverType, setHoverType] = useState("");

  const handleMouseMove = useCallback((e) => {
    if (footerRef.current) {
      const rect = footerRef.current.getBoundingClientRect();
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  }, []);

  useEffect(() => {
    const footerElement = footerRef.current;
    if (footerElement) {
      footerElement.addEventListener("mousemove", handleMouseMove);
      return () =>
        footerElement.removeEventListener("mousemove", handleMouseMove);
    }
  }, [handleMouseMove]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleMouseEnter = useCallback((type = "") => {
    setIsHovering(true);
    setHoverType(type);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    setHoverType("");
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const socialIconVariants = {
    hover: {
      scale: 1.2,
      rotate: 10,
      transition: { type: "spring", stiffness: 300 },
    },
    tap: { scale: 0.9 },
  };

  const linkVariants = {
    hover: {
      x: 10,
      color: "#ffffff", // changed from "#472ed1" to white for better contrast
      transition: { type: "tween", duration: 0.2 },
    },
  };

  // Mouse follower styles
  const getMouseFollowerSize = () => {
    if (hoverType === "button") return "60px";
    if (hoverType === "social") return "50px";
    return isHovering ? "40px" : "30px";
  };

  const getMouseFollowerBackground = () => {
    if (hoverType === "button") {
      return "radial-gradient(circle, rgba(71, 46, 209, 0.8) 0%, rgba(71, 46, 209, 0.4) 40%, transparent 70%)";
    }
    if (hoverType === "social") {
      return "radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(71, 46, 209, 0.6) 40%, transparent 70%)";
    }
    return isHovering
      ? "radial-gradient(circle, rgba(71, 46, 209, 0.6) 0%, rgba(71, 46, 209, 0.3) 40%, transparent 70%)"
      : "radial-gradient(circle, rgba(71, 46, 209, 0.4) 0%, rgba(71, 46, 209, 0.2) 40%, transparent 70%)";
  };

  const siteMapLinks = [
    "Homepage",
    "Product",
    "Resources",
    "Careers",
    "Contact",
    "Portal",
  ];
  const legalLinks = [
    "Whish-list",
    "Supports",
    "Privacy Policy",
    "Terms of Services",
    "Lawyer's Corner",
  ];
  const socialIcons = [XIcon, LinkedInIcon, FacebookIcon, Instagram];

  return (
    <Box
      ref={footerRef}
      component="footer"
      sx={{
        backgroundColor: "#472ed1",
        color: "#ffffff",
        borderRadius: "1rem",
        overflow: "hidden",
        position: "relative",
        background: "linear-gradient(135deg, #2a1a7a 0%, #0d1117 100%)",
        cursor: isMobile ? "auto" : "none",
      }}
      onMouseEnter={() => {
        if (!isMobile) handleMouseEnter();
      }}
      onMouseLeave={() => {
        if (!isMobile) handleMouseLeave();
      }}
    >
      {!isMobile && (
        <>
          {/* Mouse Follower */}
          <Box
            sx={{
              position: "absolute",
              width: getMouseFollowerSize(),
              height: getMouseFollowerSize(),
              borderRadius: "50%",
              background: getMouseFollowerBackground(),
              transform: `translate(${mousePosition.x}px, ${mousePosition.y}px) translate(-50%, -50%)`,
              transition: "all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
              pointerEvents: "none",
              zIndex: 0,
              filter: isHovering ? "blur(0px)" : "blur(1px)",
              boxShadow: isHovering
                ? `0 0 ${
                    hoverType === "button" ? "40px" : "30px"
                  } rgba(71, 46, 209, 0.6), 0 0 ${
                    hoverType === "button" ? "80px" : "60px"
                  } rgba(71, 46, 209, 0.3)`
                : "0 0 20px rgba(71, 46, 209, 0.4), 0 0 40px rgba(71, 46, 209, 0.2)",
            }}
          />

          {/* Outer Glow */}
          <Box
            sx={{
              position: "absolute",
              width: isHovering ? "120px" : "80px",
              height: isHovering ? "120px" : "80px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(71, 46, 209, 0.1) 0%, transparent 70%)",
              transform: `translate(${mousePosition.x}px, ${mousePosition.y}px) translate(-50%, -50%)`,
              transition: "all 0.3s ease-out",
              pointerEvents: "none",
              zIndex: 0,
              opacity: isHovering ? 0.8 : 0.4,
            }}
          />
        </>
      )}

      {/* Animated Background Gradient */}
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 80% 80%, rgba(71, 46, 209, 0.08) 0%, transparent 50%)",
          zIndex: 0,
        }}
        animate={{
          background: [
            "radial-gradient(circle at 80% 80%, rgba(71, 46, 209, 0.08) 0%, transparent 50%)",
            "radial-gradient(circle at 90% 70%, rgba(71, 46, 209, 0.12) 0%, transparent 60%)",
            "radial-gradient(circle at 80% 80%, rgba(71, 46, 209, 0.08) 0%, transparent 50%)",
          ],
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Logo text outline effect background */}
      <motion.svg
        ref={svgRef}
        viewBox="0 0 500 150"
        preserveAspectRatio="xMidYMid meet"
        style={{
          position: "absolute",
          bottom: "0%",
          right: "0%",
          opacity: 0.1,
          zIndex: 0,
          height: "auto",
          width: "100%",
        }}
      >
        <text
          x="69%"
          y="66%"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="64"
          fill="none"
          stroke="#472ed1"
          strokeWidth="1"
          fontFamily="Poppins, sans-serif"
        >
          LexMom
        </text>
      </motion.svg>

      {Array.from({ length: 5 }, (_, i) => (
        <motion.div
          key={i}
          style={{
            position: "absolute",
            width: "3px",
            height: "3px",
            backgroundColor: "#472ed1",
            borderRadius: "50%",
            top: `${25 + i * 12}%`,
            left: `${15 + i * 12}%`,
            zIndex: 1,
            opacity: 0.6,
          }}
          animate={{
            y: [-8, 8, -8],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 2.5 + i * 0.3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.3,
          }}
        />
      ))}

      <Container sx={{ position: "relative", zIndex: 2, py: { xs: 3, md: 4 } }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <Grid container spacing={4}>
            {/* First column: logo, description, social, back to top */}
            <Grid item xs={12} md={4}>
              <motion.div variants={itemVariants}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent={{ xs: "center", md: "flex-start" }}
                  gap={2}
                  mb={2}
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Box
                      sx={{
                        width: "8rem",
                        height: "2.5rem",
                        borderRadius: "0.5rem",
                        backgroundColor: "rgba(71, 46, 209, 0.1)",
                        padding: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={logo}
                        alt="LexMom Logo"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          filter: "brightness(0) invert(100%)",
                        }}
                        onError={(e) => {
                          e.target.src = require("../../assets/nav-lexmom.webp");
                        }}
                      />
                    </Box>
                  </motion.div>
                </Box>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#e0e0e0", // lightened for better contrast
                      lineHeight: 1.6,
                      maxWidth: 300,
                      mx: { xs: "auto", md: 0 },
                      textAlign: { xs: "center", md: "left" },
                    }}
                  >
                    The Growth Operating System for Founder-Led Law Firms.
                  </Typography>
                </motion.div>

                {/* Social Icons */}
                <Box
                  display="flex"
                  gap={1.5}
                  mt={3}
                  justifyContent={{ xs: "center", md: "flex-start" }}
                  flexWrap="wrap"
                >
                  {socialIcons.map((Icon, idx) => (
                    <motion.div
                      key={idx}
                      variants={socialIconVariants}
                      whileHover="hover"
                      whileTap="tap"
                      onMouseEnter={() => handleMouseEnter("social")}
                      onMouseLeave={handleMouseLeave}
                    >
                      <IconButton
                        color="inherit"
                        size="medium"
                        aria-label={`Social media link ${idx + 1}`}
                        sx={{
                          width: 44,
                          height: 44,
                          backgroundColor: "rgba(71, 46, 209, 0.1)",
                          border: "1px solid rgba(71, 46, 209, 0.2)",
                          color: "#472ed1",
                          "&:hover": {
                            backgroundColor: "rgba(71, 46, 209, 0.2)",
                            borderColor: "#472ed1",
                            color: "#ffffff",
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        <Icon sx={{ fontSize: "1.2rem" }} />
                      </IconButton>
                    </motion.div>
                  ))}
                </Box>

                {/* Back to Top Button */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onMouseEnter={() => handleMouseEnter("button")}
                  onMouseLeave={handleMouseLeave}
                >
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={scrollToTop}
                    startIcon={<KeyboardArrowUpIcon />}
                    sx={{
                      mt: 3,
                      width: { xs: "100%", sm: "auto" },
                      px: 3,
                      py: 1,
                      borderColor: "#472ed1",
                      color: "#472ed1",
                      borderRadius: "25px",
                      backgroundColor: "rgba(71, 46, 209, 0.05)",
                      backdropFilter: "blur(10px)",
                      fontWeight: 500,
                      "&:hover": {
                        borderColor: "#472ed1",
                        backgroundColor: "rgba(71, 46, 209, 0.15)",
                        color: "#ffffff",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(71, 46, 209, 0.3)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Back to Top
                  </Button>
                </motion.div>
              </motion.div>
            </Grid>

            {/* Site Map - now two columns on mobile */}
            <Grid item xs={6} md={4}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="subtitle1"
                  component="h3"
                  fontWeight="600"
                  gutterBottom
                  sx={{
                    color: "#ffffff",
                    mb: "0.5rem",
                    fontSize: "1.2rem",
                    fontFamily: "Italiana, serif",
                    letterSpacing: "0.2rem",
                    textAlign: "left", // always left-aligned
                  }}
                >
                  Site Map
                </Typography>
                {siteMapLinks.map((item, idx) => (
                  <motion.div
                    key={idx}
                    variants={linkVariants}
                    whileHover="hover"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * idx }}
                  >
                    <Typography
                      variant="body2"
                      component="a"
                      href="#"
                      sx={{
                        color: "#e0e0e0", // lightened
                        my: 0.8,
                        cursor: "pointer",
                        position: "relative",
                        fontSize: "0.9rem",
                        fontWeight: 400,
                        display: "block",
                        textDecoration: "none",
                        textAlign: "left",
                        "&:hover": {
                          color: "#ffffff", // white on hover
                          "&::before": {
                            width: "50%",
                          },
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          bottom: -2,
                          left: 0,
                          width: "0%",
                          height: "1px",
                          backgroundColor: "#472ed1",
                          transition: "width 0.3s ease",
                        },
                      }}
                    >
                      {item}
                    </Typography>
                  </motion.div>
                ))}
              </motion.div>
            </Grid>

            {/* Legal Links - now two columns on mobile */}
            <Grid item xs={6} md={4}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="subtitle1"
                  component="h3"
                  fontWeight="600"
                  gutterBottom
                  sx={{
                    color: "#ffffff",
                    mb: "0.5rem",
                    fontSize: "1.2rem",
                    fontFamily: "Italiana, serif",
                    letterSpacing: "0.2rem",
                    textAlign: "left",
                  }}
                >
                  Legal Links
                </Typography>
                {legalLinks.map((item, idx) => (
                  <motion.div
                    key={idx}
                    variants={linkVariants}
                    whileHover="hover"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * idx }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#e0e0e0", // lightened
                        my: 0.8,
                        cursor: "pointer",
                        position: "relative",
                        fontSize: "0.9rem",
                        fontWeight: 400,
                        display: "block",
                        textDecoration: "none",
                        textAlign: "left",
                        "&:hover": {
                          color: "#ffffff",
                          "&::before": {
                            width: "50%",
                          },
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          bottom: -2,
                          left: 0,
                          width: "0%",
                          height: "1px",
                          backgroundColor: "#472ed1",
                          transition: "width 0.3s ease",
                        },
                      }}
                      onClick={() => {
                        if (item === "Privacy Policy") {
                          navigate("/privacy-policy");
                        } else if (item === "Terms of Services") {
                          navigate("/terms-and-conditions");
                        } else if (item === "Supports") {
                          window.open(
                            "https://support.lexmom.com/en/",
                            "_blank",
                            "noopener,noreferrer",
                          );
                        } else if (item === "Whish-list") {
                          window.open(
                            "https://wishlist.lexmom.com/",
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }
                      }}
                    >
                      {item}
                    </Typography>
                  </motion.div>
                ))}
              </motion.div>
            </Grid>
          </Grid>

          <motion.div
            variants={itemVariants}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <Box
              mt={{ xs: 4, md: 6 }}
              sx={{
                borderTop: "1px solid #472ed1",
                p: "1rem 0",
                textAlign: "center",
                color: "#e0e0e0", // lightened
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                background:
                  "linear-gradient(90deg, transparent, rgba(71, 46, 209, 0.05), transparent)",
                borderRadius: "0 0 1rem 1rem",
              }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
              >
                Copyright © 2024 LexMom. All rights reserved.
              </motion.div>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Footer;
